import { FC, useState, useMemo } from "react"
import { ChevronDown, X, Filter } from "lucide-react"

interface ActivityFilterProps {
  selectedTypes: string[]
  onFilterChange: (types: string[]) => void
  onClearFilters: () => void
}

interface ActivityTypeOption {
  value: string
  label: string
  category: "lead" | "subscription" | "engagement"
}

const ACTIVITY_TYPE_OPTIONS: ActivityTypeOption[] = [
  // Lead activities
  { value: "new_lead", label: "New Lead", category: "lead" },

  // Subscription activities
  {
    value: "new_subscription",
    label: "New Subscription",
    category: "subscription"
  },
  {
    value: "intro_email_sent",
    label: "Intro Email Sent",
    category: "subscription"
  },
  {
    value: "intro_email_opened",
    label: "Intro Email Opened",
    category: "subscription"
  },
  {
    value: "intro_email_clicked",
    label: "Intro Email Clicked",
    category: "subscription"
  },
  {
    value: "sms_followup_sent",
    label: "SMS Follow-up Sent",
    category: "subscription"
  },
  { value: "unsubscribed", label: "Unsubscribed", category: "subscription" },
  { value: "reactivated", label: "Reactivated", category: "subscription" },
  {
    value: "dashboard_ai_created",
    label: "AI Advisor Initialized",
    category: "subscription"
  },

  // Engagement activities
  {
    value: "dashboard_initial_visit",
    label: "Dashboard Initial Visit",
    category: "engagement"
  },
  {
    value: "dashboard_pageview",
    label: "Dashboard Page View",
    category: "engagement"
  },
  {
    value: "dashboard_update",
    label: "Dashboard Update",
    category: "engagement"
  },
  {
    value: "dashboard_contact",
    label: "Dashboard Contact",
    category: "engagement"
  },
  {
    value: "loan_summary_pdf",
    label: "Loan Summary PDF",
    category: "engagement"
  },
  {
    value: "loan_summary_jpeg",
    label: "Loan Summary JPEG",
    category: "engagement"
  },
  {
    value: "rate_table_loaded",
    label: "Rate Table Loaded",
    category: "engagement"
  },
  { value: "pricing_shown", label: "Pricing Shown", category: "engagement" },
  { value: "pricing_empty", label: "Empty Pricing", category: "engagement" }
]

export const DEFAULT_SELECTED_TYPES = [
  "new_lead",
  "new_subscription",
  "sms_followup_sent",
  "dashboard_initial_visit"
]

const CATEGORY_LABELS = {
  lead: "Leads",
  subscription: "Subscriptions",
  engagement: "Engagement"
}

const CATEGORY_COLORS = {
  lead: "bg-emerald-100 text-emerald-700 border-emerald-200",
  subscription: "bg-violet-100 text-violet-700 border-violet-200",
  engagement: "bg-blue-100 text-blue-700 border-blue-200"
}

const ActivityFilter: FC<ActivityFilterProps> = ({
  selectedTypes,
  onFilterChange,
  onClearFilters
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const groupedOptions = useMemo(() => {
    return ACTIVITY_TYPE_OPTIONS.reduce((acc, option) => {
      if (!acc[option.category]) {
        acc[option.category] = []
      }
      acc[option.category].push(option)
      return acc
    }, {} as Record<string, ActivityTypeOption[]>)
  }, [])

  const handleToggle = (value: string) => {
    const newSelected = selectedTypes.includes(value)
      ? selectedTypes.filter(t => t !== value)
      : [...selectedTypes, value]
    onFilterChange(newSelected)
  }

  const handleSelectAll = (category: string) => {
    const categoryValues = groupedOptions[category].map(opt => opt.value)
    const allSelected = categoryValues.every(val => selectedTypes.includes(val))

    if (allSelected) {
      // Deselect all in this category
      onFilterChange(selectedTypes.filter(t => !categoryValues.includes(t)))
    } else {
      // Select all in this category
      const combined = [...selectedTypes, ...categoryValues]
      const uniqueSet = new Set(combined)
      const newSelected = Array.from(uniqueSet)
      onFilterChange(newSelected)
    }
  }

  return (
    <div className="relative">
      {/* Filter Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-200 ${
          selectedTypes.length > 0
            ? "bg-primary-50 border-primary-300 text-primary-700 hover:bg-primary-100"
            : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
        }`}
      >
        <Filter className="w-4 h-4" />
        <span className="text-sm font-medium">
          Filter Events
          {selectedTypes.length > 0 && (
            <span className="ml-1.5 px-1.5 py-0.5 bg-primary-600 text-white text-xs rounded-full">
              {selectedTypes.length}
            </span>
          )}
        </span>
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Clear Button */}
      {selectedTypes.length > 0 && (
        <button
          onClick={onClearFilters}
          className="ml-2 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 text-sm transition-colors duration-200"
        >
          <X className="w-3.5 h-3.5" />
          Clear
        </button>
      )}

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu */}
          <div className="absolute z-20 mt-2 w-80 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden animate-[fadeSlideIn_0.2s_ease-out_forwards]">
            <div className="p-4 border-b border-slate-200 bg-slate-50">
              <h3 className="text-sm font-semibold text-slate-900">
                Filter by Event Type
              </h3>
              <p className="text-xs text-slate-600 mt-1">
                Select the events you want to see
              </p>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {Object.entries(groupedOptions).map(([category, options]) => (
                <div
                  key={category}
                  className="border-b border-slate-100 last:border-b-0"
                >
                  {/* Category Header */}
                  <div className="sticky top-0 z-10 bg-white px-4 py-2 border-b border-slate-100">
                    <div className="flex items-center justify-between">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs font-medium ${
                          CATEGORY_COLORS[
                            category as keyof typeof CATEGORY_COLORS
                          ]
                        }`}
                      >
                        {
                          CATEGORY_LABELS[
                            category as keyof typeof CATEGORY_LABELS
                          ]
                        }
                      </span>
                      <button
                        onClick={() => handleSelectAll(category)}
                        className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                      >
                        {options.every(opt => selectedTypes.includes(opt.value))
                          ? "Deselect All"
                          : "Select All"}
                      </button>
                    </div>
                  </div>

                  {/* Options */}
                  <div className="p-2">
                    {options.map(option => (
                      <label
                        key={option.value}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors duration-150"
                      >
                        <input
                          type="checkbox"
                          checked={selectedTypes.includes(option.value)}
                          onChange={() => handleToggle(option.value)}
                          className="w-4 h-4 text-primary-600 border-slate-300 rounded focus:ring-2 focus:ring-primary-500 focus:ring-offset-0 cursor-pointer"
                        />
                        <span className="text-sm text-slate-700 flex-1">
                          {option.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer with count */}
            {selectedTypes.length > 0 && (
              <div className="p-3 bg-slate-50 border-t border-slate-200">
                <p className="text-xs text-slate-600 text-center">
                  {selectedTypes.length} event type
                  {selectedTypes.length !== 1 ? "s" : ""} selected
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default ActivityFilter
